import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { PROFILES, PROFILE_TABS, profileFields, allergenIds, allergenNames, WINE_COLORS, containerFor, defaultPacks, WINE_MEASURES, drinkName, formatAlcohol, formatGlasses, isProfiled, measureName, parseAlcohol, parseGlasses, profileById, profileFor, reasonLabel, ripeness, sizeOf, stockLines, tasteOf, uuidv7 } from "@lagerlog/core";
import type { GlassPrice, Profile, ProfileField } from "@lagerlog/core";
import { decimalGlasses, drinkProfile, euro, findArticles, keepsVintages, usedProfiles } from "../catalog.ts";
import type { Article, Catalog } from "../catalog.ts";
import { useCatalog, useLedger, useStore } from "../hooks.ts";
import { store as appStore } from "../store.ts";
import { TutorialsLink } from "./Login.tsx";
import { fmt } from "./Book.tsx";
import { Import } from "./Import.tsx";
import { CardView, cardOutdated } from "./Card.tsx";
import { Parts } from "./Stock.tsx";
import { useWide } from "../wide.ts";
import { Reserved } from "./Reserved.tsx";

type View = { kind: "list" } | { kind: "import" } | { kind: "card" } | { kind: "show"; id: string } | { kind: "edit"; id: string | null; from?: Article; profile?: string };

/** „Neuer Wein“, „Neue Spirituose“, „Neues Bier“. */
const NEW_ONE: Record<string, string> = { wein: "Neuer Wein", spirituosen: "Neue Spirituose", bier: "Neues Bier", alkoholfrei: "Neues Getränk", weitere: "Neues Getränk" };
const PROFILE_KEY = "lagerlog.profile";
function readProfile(): string {
  try {
    return localStorage.getItem(PROFILE_KEY) ?? "wein";
  } catch {
    return "wein";
  }
}
type Stock = Map<string, { qty: number; value: number; at: Map<string, number> }>;

/**
 * The wines of the workspace (P1-4 with the fields of the wine profile, docs/13-profil-wein.md):
 * search and filter, a wine with its stock, add one, change one, the next vintage of one.
 * On a wide screen the list stays on the left and the chosen wine shows on the right.
 */
export function Wines() {
  const { store, version } = useStore();
  const catalog = useCatalog();
  const ledger = useLedger();
  const wide = useWide();
  const [view, setView] = useState<View>({ kind: "list" });
  const [query, setQuery] = useState("");
  const [color, setColor] = useState<string | null>(null);
  // The part of the switcher (Wein | Spirituosen | Bier …); the device keeps it.
  const [profile, setProfileState] = useState(readProfile);
  const setProfile = (id: string) => {
    setProfileState(id);
    setColor(null);
    try {
      localStorage.setItem(PROFILE_KEY, id);
    } catch {
      // Only a convenience.
    }
  };

  /** Bottles per wine in the storages of this device, and per location. */
  const stock = useMemo(() => {
    const out: Stock = new Map();
    if (!ledger) return out;
    const scope = new Set(store.config?.lagers ?? []);
    for (const line of stockLines(ledger)) {
      if (!scope.has(line.lager)) continue;
      const s = out.get(line.article) ?? { qty: 0, value: 0, at: new Map<string, number>() };
      s.qty += line.qty;
      s.value += line.value;
      s.at.set(line.location, (s.at.get(line.location) ?? 0) + line.qty);
      out.set(line.article, s);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger, version]);

  if (!catalog || !ledger) return null;

  if (view.kind === "import") return <Import onDone={() => setView({ kind: "list" })} />;
  if (view.kind === "card") return <CardView onDone={() => setView({ kind: "list" })} />;

  if (view.kind === "edit") {
    const article = view.id ? catalog.articles.get(view.id) : undefined;
    return <WineForm article={article} from={view.from} profileId={view.profile ?? profile} wide={wide} onDone={(id) => setView(id ? { kind: "show", id } : { kind: "list" })} />;
  }

  const shownId = view.kind === "show" ? view.id : null;
  const detail = shownId ? catalog.articles.get(shownId) : undefined;
  const list = (
    <WineList
      catalog={catalog}
      stock={stock}
      query={query}
      setQuery={setQuery}
      color={color}
      setColor={setColor}
      profile={usedProfiles(catalog).includes(profile) ? profile : "wein"}
      setProfile={setProfile}
      selected={wide ? shownId : null}
      onShow={(id) => setView({ kind: "show", id })}
      onNew={(id) => setView({ kind: "edit", id: null, profile: id })}
      onImport={() => setView({ kind: "import" })}
      onCard={() => setView({ kind: "card" })}
      own={store.config?.kind === "private"}
      outdated={cardOutdated(catalog, ledger, store.config?.kind === "private")}
    />
  );

  if (!wide) {
    if (detail) return <WineDetail a={detail} catalog={catalog} stock={stock} onBack={() => setView({ kind: "list" })} setView={setView} />;
    return list;
  }
  return (
    <div className="split">
      <div className="pane">{list}</div>
      <div className="pane side-pane">
        {detail ? (
          <WineDetail a={detail} catalog={catalog} stock={stock} setView={setView} />
        ) : (
          <div className="card placeholder">
            <p>Links {profile === "wein" ? "einen Wein" : "ein Getränk"} wählen: Hier stehen dann Bestand je Lagerort, Wert und alle Angaben.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function WineDetail({ a, catalog, stock, onBack, setView }: { a: Article; catalog: Catalog; stock: Stock; onBack?: () => void; setView: (v: View) => void }) {
  const s = stock.get(a.id);
  const f = a.fields;
  const profile = profileFor(f);
  // Without vintages (F23) only a wine that has one is kept by vintage.
  const vintagesOn = keepsVintages(catalog);
  const byVintage = vintagesOn || typeof f.vintage === "number";
  const vintages = typeof f.family === "string" ? [...catalog.articles.values()].filter((o) => o.fields.family === f.family && o.id !== a.id) : [];
  return (
    <div className="wines">
      {onBack && (
        <button className="link" onClick={onBack}>
          ← Alle {profile.vocabulary.articles}
        </button>
      )}
      <h2 data-testid="wine-title">{a.name}</h2>
      <p className="muted">{[f.listNo ? `Nr. ${f.listNo}` : "", f.color, f.country, f.region].filter(Boolean).join(" · ")}</p>
      <div className="card">
        <div className="row between">
          <strong>Bestand</strong>
          <strong data-testid="wine-stock">{fmt(a, s?.qty ?? 0)}</strong>
        </div>
        {s && s.at.size > 0 && (
          <ul className="detail">
            {[...s.at.entries()]
              .filter(([, q]) => q !== 0)
              .map(([loc, q]) => {
                const l = catalog.locations.get(loc);
                return (
                  <li key={loc} className="row between">
                    <span>
                      {catalog.lagers.get(l?.lager ?? "")?.name} · {l?.name ?? loc}
                    </span>
                    <span>{fmt(a, q)}</span>
                  </li>
                );
              })}
          </ul>
        )}
        {s && s.value > 0 && <p className="muted">Wert zum Einkaufspreis: {euro(s.value)}</p>}
        <div className="buttons">
          <a className="button primary" href={`#/buchen?einlagern=${encodeURIComponent(a.id)}`} data-testid="wine-receive">
            Einlagern
          </a>
          <a className="button" href={`#/buchen?entnehmen=${encodeURIComponent(a.id)}`}>
            Entnehmen
          </a>
        </div>
      </div>
      <table className="facts">
        <tbody>
          {profileFields(profile, workspaceKind()).filter((field) => shown(f, field, vintagesOn, decimalGlasses(catalog)) !== "").map((field) => (
            <tr key={field.key}>
              <th>{field.label}</th>
              <td>{shown(f, field, vintagesOn, decimalGlasses(catalog))}</td>
            </tr>
          ))}
          {a.barcodes.length > 0 && (
            <tr>
              <th>Strichcode</th>
              <td>{a.barcodes.join(", ")}</td>
            </tr>
          )}
          {a.packs.length > 0 && (
            <tr>
              <th>Verpackung</th>
              <td>{a.packs.map((p) => p.name).join(", ")}</td>
            </tr>
          )}
        </tbody>
      </table>
      {!isProfiled(f) && profile.id === "wein" && <p className="muted">Ein Wein der Demo: Er hat nur einen Namen. Mit „Bearbeiten“ lassen sich die Felder ergänzen.</p>}
      {vintages.length > 0 && (
        <>
          <h3>{profile.id === "wein" && byVintage ? "Andere Jahrgänge" : "Andere Größen"}</h3>
          <ul className="list">
            {vintages.map((o) => (
              <li key={o.id}>
                <button className="row between" onClick={() => setView({ kind: "show", id: o.id })}>
                  <span>{o.name}</span>
                  <span className="side muted">{fmt(o, stock.get(o.id)?.qty ?? 0)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <Reserved a={a} catalog={catalog} stock={s?.qty ?? 0} />
      <History a={a} catalog={catalog} />
      <div className="buttons">
        <button onClick={() => setView({ kind: "edit", id: a.id })} data-testid="wine-edit">
          Bearbeiten
        </button>
        {profile.fields.some((field) => field.key === "vintage") && byVintage && (
          <button onClick={() => setView({ kind: "edit", id: null, from: a })} data-testid="wine-next-vintage">
            Neuer Jahrgang
          </button>
        )}
      </div>
    </div>
  );
}

const VERB: Record<string, string> = { in: "Eingelagert", out: "Entnommen", move: "Umgelagert", count: "Gezählt" };

/** The last bookings of a wine (R6); all of them in the protocol. */
export function History({ a, catalog }: { a: Article; catalog: Catalog }) {
  const { store } = useStore();
  const journal = store.replica?.journal() ?? [];
  const cancelled = new Set(journal.filter((b) => b.kind === "cancel").map((b) => (b.kind === "cancel" ? b.cancels : "")));
  const own = journal.filter((b) => b.kind !== "cancel" && b.kind !== "convert" && b.article === a.id && !cancelled.has(b.id)).reverse();
  if (own.length === 0) return null;
  const place = (id: string) => catalog.locations.get(id)?.name ?? id;
  return (
    <>
      <h3>Verlauf</h3>
      <ul className="list">
        {own.slice(0, 8).map((b) => {
          if (b.kind === "cancel" || b.kind === "convert") return null;
          const where = b.kind === "move" ? `${place(b.from)} → ${place(b.to)}` : `${b.kind === "in" || b.kind === "out" ? `${reasonLabel(b.reason, a.fields)} · ` : ""}${place(b.location)}`;
          return (
            <li key={b.id} className="row between">
              <span>
                <span className="muted">
                  {new Date(b.at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {store.personName(b.person) ?? b.device ?? ""}
                </span>
                <br />
                {VERB[b.kind]} · {where}
              </span>
              <span className="side">
                <Parts a={a} qty={b.qty} />
              </span>
            </li>
          );
        })}
      </ul>
      <a href={`#/protokoll?wein=${encodeURIComponent(a.id)}`}>Ganzer Verlauf im Logbuch →</a>
    </>
  );
}

function WineList(props: {
  catalog: Catalog;
  stock: Stock;
  query: string;
  setQuery: (q: string) => void;
  color: string | null;
  setColor: (c: string | null) => void;
  selected: string | null;
  onShow: (id: string) => void;
  onNew: (profile: string) => void;
  onImport: () => void;
  profile: string;
  setProfile: (id: string) => void;
  onCard: () => void;
  /** A private cellar: the list of the cellar instead of the card for guests. */
  own: boolean;
  /** The printed card no longer says what is true. */
  outdated: boolean;
}) {
  const { catalog, stock, query, color } = props;
  const profile = profileById(props.profile);
  const used = usedProfiles(catalog);
  const all = [...catalog.articles.values()].filter((a) => drinkProfile(a) === profile.id);
  const kinds = profile.fields.find((f) => f.key === profile.categoryField)?.options ?? [];
  const colors = kinds.filter((c) => all.some((a) => (a.fields.color ?? a.category) === c));
  const year = new Date().getFullYear();
  const ready = (a: Article) => ["ready", "last"].includes(ripeness(a.fields, year) ?? "");
  const anyWindow = all.some((a) => ripeness(a.fields, year) !== null);
  // Wines marked „Zum Kochen“, for the kitchen (docs/13-profil-wein.md); only when there are any.
  const anyCooking = all.some((a) => a.fields.cooking === true);
  // With a query: best fit first; without: by colour and name.
  const rows = (query.trim() ? findArticles(all, query) : [...all].sort((a, b) => sortKey(a, kinds).localeCompare(sortKey(b, kinds), "de"))).filter(
    (a) => !color || (color === READY ? ready(a) : color === COOKING ? a.fields.cooking === true : (a.fields.color ?? a.category) === color),
  );
  const others = PROFILES.filter((p) => p.id !== profile.id);

  return (
    <div className="wines">
      {used.length >= 5 ? (
        // Five parts do not fit on a phone („Spirituo…“): a choice instead (Martin, 18.10.2026).
        <label className="profile-select">
          <select value={profile.id} onChange={(e) => props.setProfile(e.target.value)} data-testid="profile-switch">
            {used.map((id) => (
              <option key={id} value={id}>
                {PROFILE_TABS[id]}
              </option>
            ))}
          </select>
        </label>
      ) : used.length > 1 && (
        <div className="segmented profiles" data-testid="profile-switch">
          {used.map((id) => (
            <button key={id} className={id === profile.id ? "active" : ""} onClick={() => props.setProfile(id)} data-testid={`profile-${id}`}>
              {PROFILE_TABS[id]}
            </button>
          ))}
        </div>
      )}
      <div className="row">
        <input
          placeholder={`${profile.id === "wein" ? "Wein, Erzeuger, Region, Rebsorte" : `${profile.vocabulary.article}, Hersteller`} oder ${profileFields(profile, workspaceKind()).find((f) => f.key === "listNo")?.label ?? "Kartennummer"}`}
          value={query}
          onChange={(e) => props.setQuery(e.target.value)}
          data-testid="wine-search"
        />
        <button className="primary" onClick={() => props.onNew(profile.id)} data-testid="wine-new">
          + {profile.vocabulary.article}
        </button>
      </div>
      {(colors.length > 1 || anyWindow || anyCooking) && (
        <div className="chips filters">
          {colors.length > 1 &&
            colors.map((c) => (
              <button key={c} className={color === c ? "active" : ""} onClick={() => props.setColor(color === c ? null : c)}>
                {c}
              </button>
            ))}
          {anyWindow && (
            <button className={color === READY ? "active" : ""} onClick={() => props.setColor(color === READY ? null : READY)} data-testid="filter-ready">
              trinkreif
            </button>
          )}
          {anyCooking && (
            <button className={color === COOKING ? "active" : ""} onClick={() => props.setColor(color === COOKING ? null : COOKING)} data-testid="filter-cooking">
              zum Kochen
            </button>
          )}
        </div>
      )}
      <p className="row between">
        <span className="muted">
          {rows.length} {rows.length === 1 ? profile.vocabulary.article : profile.vocabulary.articles}
        </span>
        <span className="row">
          <button className="link" onClick={props.onCard} data-testid="wine-card">
            {props.own ? "Kellerliste …" : "Getränkekarte …"}
            {props.outdated && <span className="tag" data-testid="card-outdated">veraltet</span>}
          </button>
          <button className="link" onClick={props.onImport} data-testid="wine-import">
            Excel-Tabelle …
          </button>
        </span>
      </p>
      <ul className="list">
        {rows.map((a) => {
          const s = stock.get(a.id);
          const sub = [a.fields.listNo ? `Nr. ${a.fields.listNo}` : "", a.fields.color ?? a.category, a.fields.region, typeof a.fields.salePrice === "number" ? euro(a.fields.salePrice) : ""].filter(Boolean).join(" · ");
          return (
            <li key={a.id} className={props.selected === a.id ? "selected" : ""}>
              <button className={`row between ${s && s.qty < 0 ? "negative" : ""}`} onClick={() => props.onShow(a.id)}>
                <span>
                  {a.name}
                  {sub && (
                    <>
                      <br />
                      <span className="muted">{sub}</span>
                    </>
                  )}
                </span>
                <span className={`side ${s?.qty ? "" : "muted"}`}>
                  {(s?.qty ? fmt(a, s.qty) : "–").split(" + ").map((part, i, parts) => (
                    <span key={i}>{i < parts.length - 1 ? `${part} +` : part}</span>
                  ))}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {all.length === 0 && (
        <p>
          Noch keine {profile.vocabulary.articles}. Leg {profile.id === "wein" ? "den ersten" : "eins"} mit „+ {profile.vocabulary.article}“ an, oder übernimm viele auf einmal aus einer Excel-Tabelle.
          <br />
          <span className="muted">
            Wie fange ich an? <TutorialsLink />
          </span>
        </p>
      )}
      <label className="other-drink">
        <span className="muted">Anderes Getränk anlegen:</span>
        <select value="" onChange={(e) => e.target.value && props.onNew(e.target.value)} data-testid="drink-new">
          <option value="">bitte wählen</option>
          {others.map((p) => (
            <option key={p.id} value={p.id}>
              {NEW_ONE[p.id]} ({PROFILE_TABS[p.id]})
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** The filter chip for drinks in their drinking window. */
const READY = "\u0000trinkreif";
/** The filter chip for wines marked „Zum Kochen“. */
const COOKING = "\u0000kochen";
const sortKey = (a: Article, kinds: readonly string[] = WINE_COLORS) => `${String(kinds.indexOf(String(a.fields.color ?? a.category)) + 1 || 99).padStart(2, "0")}|${a.name}`;

/** A field's value for reading; `vintages: false` (F23): an empty vintage is not shown as „ohne Jahrgang“. */
export function shown(f: Record<string, unknown>, field: ProfileField, vintages = true, decimal = false): string {
  if (field.type === "range") {
    const from = f[`${field.key}From`];
    const to = f[`${field.key}To`];
    return from || to ? `${from ?? "…"}–${to ?? "…"}` : "";
  }
  const v = f[field.key];
  if (v === undefined || v === null || v === "") return field.key === "vintage" && isProfiled(f) && vintages ? "ohne Jahrgang" : "";
  if (field.type === "alcohol") return formatAlcohol(v) ? `${formatAlcohol(v)} % vol.` : "";
  if (field.type === "money" && typeof v === "number") return euro(v);
  if (field.type === "yesno") return v ? "ja" : "";
  if (field.type === "glass") return Array.isArray(v) ? formatGlasses(v as GlassPrice[], glassContext(f, decimal)).replace(/(\d,\d\d)/g, "$1 €") : "";
  if (field.type === "size") return sizeText(sizeOf(f));
  if (field.key === "allergens") return allergenNames(v).join(", ");
  if (Array.isArray(v)) return v.join(", ");
  const text = typeof v === "number" ? String(v).replace(".", ",") : String(v);
  return field.unit && field.type !== "money" ? `${text} ${field.unit}` : text;
}

/** A field's value as the form edits it. */
/** How a drink's glass sizes are named: by its profile and kind, decimals as the workspace chose (B5). */
const glassContext = (f: Record<string, unknown>, decimal: boolean) => ({ profile: profileFor(f).measures ?? WINE_MEASURES, ...(typeof f.color === "string" ? { kind: f.color } : {}), decimal });

export function editable(f: Record<string, unknown>, key: string, type: ProfileField["type"], decimal = false): string {
  const v = f[key];
  if (v === undefined || v === null) return "";
  if (type === "money" && typeof v === "number") return (v / 100).toFixed(2).replace(".", ",");
  if (type === "glass") return Array.isArray(v) ? formatGlasses(v as GlassPrice[], glassContext(f, decimal)) : "";
  if (type === "size") return String(sizeOf(f) ?? "");
  if (type === "alcohol") return formatAlcohol(v);
  if (key === "allergens") return allergenNames(v).join(", ");
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "1" : "";
  return String(v).replace(".", type === "number" ? "," : ".");
}

/** Business or private cellar, for the words of the profile. */
const workspaceKind = () => (appStore.config?.kind === "private" ? "private" : "business");

/** The form for a new wine, a change, or the next vintage of a wine (from). */
function WineForm({ article, from, profileId, wide = false, onDone }: { article?: Article; from?: Article; profileId: string; wide?: boolean; onDone: (id: string | null) => void }) {
  const { store } = useStore();
  const catalog = useCatalog();
  const known = article ?? from;
  // The profile of the drink: of the one edited, else the one chosen (docs/17-getraenke.md).
  const profileOfForm: Profile = known ? profileFor(known.fields) : profileById(profileId);
  // The profile with the words of this kind of workspace (B7: „Nummer“ in a private cellar).
  const P: Profile = { ...profileOfForm, fields: profileFields(profileOfForm, workspaceKind()) };
  const sizes = P.fields.find((f) => f.key === "size")?.sizes ?? [750];
  const base = known?.fields ?? P.defaults ?? {};
  // Without vintages (F23) the vintage waits under „Mehr“, unless the wine is kept by vintage.
  const vintagesOn = catalog ? keepsVintages(catalog) : true;
  const byVintage = vintagesOn || typeof base.vintage === "number" || Boolean(from);
  const groupOf = (field: ProfileField) => (field.key === "vintage" && !byVintage ? "more" : field.group);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const field of P.fields) {
      if (field.type === "range") {
        v[`${field.key}From`] = editable(base, `${field.key}From`, "year");
        v[`${field.key}To`] = editable(base, `${field.key}To`, "year");
      } else v[field.key] = editable(base, field.key, field.type, decimalGlasses(catalog));
    }
    if (!article && !from) v.size = String(P.usualSize ?? (sizes.includes(500) ? 500 : sizes[0]));
    if (from) {
      const last = typeof base.vintage === "number" ? base.vintage : new Date().getFullYear() - 1;
      v.vintage = String(last + 1);
    }
    // A demo wine has only a name: start with it as the wine's name.
    if (article && !isProfiled(base) && P.id === "wein") v.wine = article.name;
    return v;
  });
  const [barcode, setBarcode] = useState((article ?? from)?.barcodes.join(", ") ?? "");
  // Cartons and crates by name (a crate and a tray may both hold 24); stored in ml (times the size).
  const [packs, setPacks] = useState<Set<string>>(() => new Set(((article ?? from)?.packs ?? defaultPacks(P, P.usualSize ?? (sizes.includes(500) ? 500 : sizes[0]!))).map((p) => p.name)));
  const [error, setError] = useState<string | null>(null);
  // On a wide screen all groups are open, side by side.
  const [open, setOpen] = useState<Set<string>>(new Set(wide ? Object.keys(P.groups) : ["main", "gastro"]));
  // The size is fixed once something was booked: 4 bottles of 0,75 l must not turn into 2 Magnums.
  const booked = useMemo(() => Boolean(article && store.replica?.journal().some((b) => "article" in b && b.article === article.id)), [article, store.replica]);

  const kegForm = P.bulk !== undefined && containerFor(P, Number(values.size) || null, values.container?.trim() ?? "") === P.bulk.container;
  const set = (key: string, value: string) => {
    const next = { ...values, [key]: value };
    if (key === "size" || key === "container") {
      // A beer of 30 l is a keg; a keg has no crate, a can its tray (defaultPacks).
      const ml = Number(next.size);
      if (key === "size" && !next.container?.trim()) next.container = containerFor(P, ml, "") ?? "";
      if (ml > 0) setPacks(new Set(defaultPacks(P, ml, next.container?.trim() || undefined).map((p) => p.name)));
    }
    setValues(next);
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const missing = P.fields.filter((field) => field.required && field.key !== "vintage" && !values[field.key]?.trim()).map((field) => field.label);
    if (missing.length) return setError(`Bitte ausfüllen: ${missing.join(", ")}`);
    const fields: Record<string, unknown> = {};
    for (const field of P.fields) {
      if (field.type === "range") {
        for (const end of ["From", "To"]) {
          const n = parseInt(values[`${field.key}${end}`] ?? "", 10);
          fields[`${field.key}${end}`] = Number.isFinite(n) ? n : null;
        }
        continue;
      }
      const raw = (values[field.key] ?? "").trim();
      let value: unknown = raw === "" ? null : raw;
      if (raw !== "") {
        if (field.type === "year") {
          const n = parseInt(raw, 10);
          if (!Number.isFinite(n) || n < 1800 || n > 2100) return setError(`${field.label}: bitte ein Jahr wie 2021, oder leer für „ohne Jahrgang“`);
          value = n;
        } else if (field.type === "number") {
          const n = Number(raw.replace(",", "."));
          if (!Number.isFinite(n)) return setError(`${field.label}: bitte eine Zahl`);
          value = n;
        } else if (field.type === "money") {
          const n = Number(raw.replace(/\s|€/g, "").replace(",", "."));
          if (!Number.isFinite(n) || n < 0) return setError(`${field.label}: bitte einen Betrag wie 39,50`);
          value = Math.round(n * 100);
        } else if (field.type === "multi") {
          value = raw.split(",").map((s) => s.trim()).filter(Boolean);
          if (field.key === "allergens") value = allergenIds(value);
        } else if (field.type === "yesno") {
          value = raw === "1";
        } else if (field.type === "glass") {
          const parsed = parseGlasses(raw);
          if ("error" in parsed) return setError(`${field.label}: ${parsed.error}`);
          value = parsed.value;
        } else if (field.type === "size") {
          value = Number(raw);
        } else if (field.type === "alcohol") {
          const parsed = parseAlcohol(raw);
          if ("error" in parsed) return setError(`${field.label}: bitte eine Zahl wie 13,5 oder eine Obergrenze wie < 0,5`);
          value = parsed.value;
        }
      }
      fields[field.key] = value;
    }
    const codes = barcode.split(/[\s,]+/).filter(Boolean);
    if (codes.some((c) => !/^\d{8,14}$/.test(c))) return setError("Strichcode: 8 bis 14 Ziffern");
    // A beer of 30 l without container is a keg, also one made before; a keg has no price for the whole.
    if (P.fields.some((f) => f.key === "container") && !fields.container) fields.container = containerFor(P, fields.size as number, "") ?? null;
    if (kegForm) fields.salePrice = null;
    const id = article?.id ?? uuidv7(Date.now());
    const family = (typeof base.family === "string" && base.family) || uuidv7(Date.now());
    const all = {
      ...fields,
      family,
      name: drinkName({ ...fields, profile: P.id }, { vintages: vintagesOn }),
      category: fields.color,
      profile: P.id,
      unit: "ml",
      tracking: P.tracking,
      barcodes: codes,
      packs: P.packs.filter((p) => packs.has(p.name)).map(({ sizes: _, containers: _c, ...p }) => ({ ...p, factor: p.factor * (fields.size as number) })),
    };
    // Only what changed: another device may have changed other fields meanwhile.
    const changed = Object.fromEntries(
      Object.entries(all).filter(([k, v]) => (article ? JSON.stringify(article.fields[k] ?? null) !== JSON.stringify(v ?? null) : v !== null)),
    );
    if (Object.keys(changed).length > 0) await store.edit("article", id, changed);
    // Fields of the wine family hold for every vintage.
    if (article && catalog) {
      const familyKeys = P.fields.filter((field) => field.level === "family").map((field) => field.key);
      const familyChanged = Object.fromEntries(Object.entries(changed).filter(([k]) => familyKeys.includes(k)));
      if (Object.keys(familyChanged).length > 0) {
        for (const other of catalog.articles.values()) {
          if (other.id === id || other.fields.family !== family) continue;
          await store.edit("article", other.id, { ...familyChanged, name: drinkName({ ...other.fields, ...familyChanged }, { vintages: vintagesOn }), ...(familyChanged.color ? { category: familyChanged.color } : {}) });
        }
      }
    }
    onDone(id);
  }

  const groups = Object.entries(P.groups) as [ProfileField["group"], string][];
  return (
    <form className={`wine-form ${wide ? "wide" : ""}`} noValidate onSubmit={save}>
      <button type="button" className="link" onClick={() => onDone(article?.id ?? null)}>
        ← Abbrechen
      </button>
      <h2>{article ? `${P.vocabulary.article} bearbeiten` : from ? `Neuer Jahrgang: ${from.fields.producer ?? ""} ${from.fields.wine ?? from.name}` : NEW_ONE[P.id]}</h2>
      <div className="groups">
      {groups.map(([group, title]) => (
        <section key={group} className="card">
          <button type="button" className="group-head" onClick={() => setOpen(toggle(open, group))} aria-expanded={open.has(group)}>
            <strong>{title}</strong>
            <span className="muted">{open.has(group) ? "−" : "+"}</span>
          </button>
          {open.has(group) && (
            <>
              {/* A keg is sold by the glass only: no price for the whole (drinks-card.ts). */}
              {P.fields.filter((field) => groupOf(field) === group && !(field.key === "salePrice" && kegForm)).map((field) => (
                <FieldInput key={field.key} field={field} values={values} set={set} locked={field.type === "size" && booked} />
              ))}
              {group === "more" && (
                <label>
                  Strichcode
                  <input inputMode="numeric" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
                  <span className="hint">Mehrere durch Komma getrennt. Viele Flaschen haben keinen.</span>
                </label>
              )}
              {group === "main" && (
                <div className="packs">
                  <span className="muted">Verpackung</span>
                  {/* Only the packs of this container (no crate for a keg); one chosen before stays to be taken off. */}
                  {P.packs.filter((p) => packs.has(p.name) || !p.containers || p.containers.includes(values.container?.trim() || "Flasche")).map((p) => (
                    <label key={p.name} className="check">
                      <input type="checkbox" checked={packs.has(p.name)} onChange={() => setPacks(toggle(packs, p.name))} />
                      {p.name}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      ))}
      </div>
      {article && P.id === "wein" && byVintage && <p className="hint">Erzeuger, Name, Farbe, Herkunft, Rebsorten, Geschmack, Kartentext und Notiz gelten für alle Jahrgänge dieses Weins.</p>}
      {error && <p className="error-box">{error}</p>}
      <button className="primary big" data-testid="wine-save">
        Speichern
      </button>
    </form>
  );
}

function toggle<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** A size as shown in the form and the wine: „0,75 l“, „Magnum 1,5 l“. */
const sizeText = (ml: number | null) => (ml === null ? "" : measureName(ml, "size", { profile: WINE_MEASURES }));

export function FieldInput({ field, values, set, locked = false }: { field: ProfileField; values: Record<string, string>; set: (key: string, value: string) => void; locked?: boolean }) {
  const label = `${field.label}${field.required && field.key !== "vintage" ? " *" : ""}${field.unit ? ` (${field.unit})` : ""}`;
  // The taste of a still wine as residual sugar and acidity say it (tasteOf), next to the choice.
  const num = (k: string) => (values[k] ? Number(values[k]!.replace(",", ".")) : undefined);
  const suggested = field.key === "taste" && values.color !== "Schaumwein" ? tasteOf(num("sugar"), num("acidity")) : null;
  const hint =
    suggested && suggested !== values.taste ? (
      <span className="hint" data-testid="taste-suggestion">
        Nach Restzucker und Säure: {suggested}{" "}
        <button type="button" className="link" onClick={() => set("taste", suggested)}>
          übernehmen
        </button>
      </span>
    ) : (
      field.hint && <span className="hint">{field.hint}</span>
    );
  const testid = `field-${field.key}`;
  if (field.type === "range") {
    return (
      <label>
        {label}
        <span className="row">
          <input inputMode="numeric" placeholder="von" value={values[`${field.key}From`] ?? ""} onChange={(e) => set(`${field.key}From`, e.target.value)} />
          <input inputMode="numeric" placeholder="bis" value={values[`${field.key}To`] ?? ""} onChange={(e) => set(`${field.key}To`, e.target.value)} />
        </span>
        {hint}
      </label>
    );
  }
  if (field.type === "yesno") {
    return (
      <label className="check">
        <input type="checkbox" checked={values[field.key] === "1"} onChange={(e) => set(field.key, e.target.checked ? "1" : "")} />
        {field.label}
      </label>
    );
  }
  if (field.type === "choice") {
    return (
      <label>
        {label}
        <select value={values[field.key] ?? ""} onChange={(e) => set(field.key, e.target.value)} disabled={locked} data-testid={testid}>
          {!field.required && <option value="">–</option>}
          {field.required && !values[field.key] && <option value="">bitte wählen</option>}
          {field.options?.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {locked ? <span className="hint">Fest, weil schon gebucht wurde.</span> : hint}
      </label>
    );
  }
  if (field.type === "size") {
    const current = Number(values[field.key]);
    const sizes = field.sizes ?? [];
    return (
      <label>
        {field.label}
        {field.required ? " *" : ""}
        <select value={values[field.key] ?? ""} onChange={(e) => set(field.key, e.target.value)} disabled={locked} data-testid={testid}>
          {!values[field.key] && <option value="">bitte wählen</option>}
          {[...sizes, ...(current && !sizes.includes(current) ? [current] : [])].map((ml) => (
            <option key={ml} value={String(ml)}>
              {sizeText(ml)}
            </option>
          ))}
        </select>
        <span className="hint">{locked ? "Fest, weil schon gebucht wurde. Eine andere Größe ist ein eigener Wein." : field.hint}</span>
      </label>
    );
  }
  if (field.type === "longtext") {
    return (
      <label>
        {label}
        <textarea rows={3} value={values[field.key] ?? ""} onChange={(e) => set(field.key, e.target.value)} data-testid={testid} />
        {hint}
      </label>
    );
  }
  const list = field.options ? `options-${field.key}` : undefined;
  return (
    <label>
      {label}
      <input
        value={values[field.key] ?? ""}
        onChange={(e) => set(field.key, e.target.value)}
        inputMode={field.type === "year" ? "numeric" : field.type === "money" || field.type === "number" ? "decimal" : undefined}
        list={field.type === "multi" ? undefined : list}
        data-testid={testid}
      />
      {list && field.type !== "multi" && (
        <datalist id={list}>
          {field.options!.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
      {field.type === "multi" && field.options && <GrapeChips value={values[field.key] ?? ""} options={field.options} onChange={(v) => set(field.key, v)} />}
      {hint}
    </label>
  );
}

/** Taps add a grape to the comma-separated list; typing still works for any other. */
function GrapeChips({ value, options, onChange }: { value: string; options: readonly string[]; onChange: (v: string) => void }) {
  const chosen = value.split(",").map((s) => s.trim()).filter(Boolean);
  const last = value.endsWith(",") || value.trim() === "" ? "" : (chosen.at(-1) ?? "").toLowerCase();
  const suggestions = options.filter((o) => !chosen.includes(o) && (last === "" ? false : o.toLowerCase().startsWith(last))).slice(0, 6);
  if (suggestions.length === 0) return null;
  return (
    <span className="chips">
      {suggestions.map((o) => (
        <button type="button" key={o} onClick={() => onChange([...chosen.slice(0, -1), o].join(", ") + ", ")}>
          {o}
        </button>
      ))}
    </span>
  );
}
